import type { PopulatedEntitySuggestion } from '@bliks-io/contracts';
import { ProcessDetailsDialog } from '@/components/processes/ProcessDetailsDialog';
import { readDialogLoadState } from '@/lib/dialogLoadState';
import { useRecordingProcess } from '../hooks/useRecordingProcess';

interface SuggestionProcessPreviewDialogProps {
  suggestion: Pick<PopulatedEntitySuggestion, 'sourceProcessId' | 'sourceRecordingId'>;
  open: boolean;
  onClose: () => void;
}

/**
 * Shows the originating process's details for a suggestion, reusing the same
 * dialog as the step-recording page. Step-recording-sourced suggestions don't
 * carry the process id, so it's resolved from the recording (the same lookup
 * the step preview uses). Start-event suggestions carry it on
 * `sourceProcessId` — set directly on legacy rows, and served as a derived
 * read-projection for node-anchored ones (their `sourceRecordingId` names a
 * start event node, which the recording endpoint would not resolve) — so the
 * recording fetch is skipped whenever a process id is already known.
 * Fetches are gated on `open` so a closed dialog costs nothing.
 */
export function SuggestionProcessPreviewDialog({
  suggestion,
  open,
  onClose
}: SuggestionProcessPreviewDialogProps) {
  const { recordingQuery, processQuery, recordingWithoutStep } = useRecordingProcess(
    open ? (suggestion.sourceRecordingId ?? '') : '',
    open ? suggestion.sourceProcessId : undefined
  );

  if (!open) return null;

  const nothingToFetch = !suggestion.sourceProcessId && !suggestion.sourceRecordingId;

  return (
    <ProcessDetailsDialog
      open
      onOpenChange={(next) => !next && onClose()}
      process={processQuery.data?.process}
      loadState={readDialogLoadState(
        [recordingQuery, processQuery],
        nothingToFetch || recordingWithoutStep
      )}
    />
  );
}
